import { Component, Prop, h, Event, EventEmitter } from '@stencil/core';
import type { IconColor } from '../../../../../shared-assets/utils/PropTypes';
import clsx from 'clsx';

/**
 * Used for adding icons from Antiqua icon library.
 * Check figma <a href="https://www.figma.com/design/X4B9kN0qCP6rPWZoO7h5La/Antiqua-DLS-Guide-v1.0?node-id=1219-31919&t=QG86VYwv0PJ44mvq-0" target="_blank">here</a>
 */
@Component({
  tag: 'md-icon',
  styleUrl: 'md-icon.css',
  shadow: true,
})
export class MdIcon {
  /**
   * Icon default alt text attribute
   */
  @Prop() alttext: string;

  /**
   * Icon custom alt text attribute
   */
  @Prop() customalttext: string;

  /**
   * Color of the icon
   */
  @Prop() color: IconColor = 'currentColor';

  /**
   * Hover color of the icon
   */
  @Prop() colorhover: IconColor = 'currentColor';

  /**
   * Rotate the icon in the specified direction
   */
  @Prop() rotate:
    | 'up'
    | 'down'
    | 'left'
    | 'right'
    | 'up-right'
    | 'right-down'
    | 'down-left'
    | 'left-up' = 'up';

  /**
   * Animate the icon in the specified direction
   */
  @Prop() animation: 'spin-left' | 'spin-right' | 'none' = 'none';

  /**
   * Animation duration
   */
  @Prop() duration = '2s';

  /**
   * Show title attribute
   */
  @Prop() icontitle: 'enabled' | 'disabled' = 'enabled';

  /**
   * Size of the icon
   */
  @Prop() size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'sm';

  /**
   * Icon as button or icon
   */
  @Prop() as: 'icon' | 'button' = 'icon';

  /**
   *  Sets the tabidx(tabindex) attribute on the Icon.
   * (setting the value to be -1 removes the icon from the tab order, so users can't focus it using the keyboard).
   * This is only effective if icon uses as prop value to be button.
   */
  @Prop() tabidx = 0;

  /**
   * Icon click handler
   */
  @Event({ eventName: 'handleIconClick' }) handleIconClick: EventEmitter<
    MouseEvent | KeyboardEvent
  >;

  private iconClick = (event: MouseEvent | KeyboardEvent) => {
    event.stopPropagation();
    this.handleIconClick.emit(event);
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.iconClick(event);
    }
  };

  render() {
    const iconAlttext = this.customalttext || this.alttext;
    return (
      <span
        title={this.icontitle == 'enabled' ? iconAlttext : ''}
        class={clsx(
          'md-icon',
          `md-icon-rotate--${this.rotate}`,
          `md-icon-size--${this.size}`,
          this.color && `md-icon-color--${this.color}`,
          this.animation && `md-icon-animate--${this.animation}`,
          this.colorhover && `md-icon-color-hover--${this.colorhover}`,
          this.as && `md-icon--as-${this.as}`,
        )}
        part="md-icon"
        style={{ animationDuration: this.duration }}
        role={this.as === 'button' ? this.as : undefined}
        onClick={this.as === 'button' ? this.iconClick : undefined}
        tabindex={this.as === 'button' ? this.tabidx : null}
        onKeyDown={this.as === 'button' ? this.handleKeyDown : undefined}
      >
        <slot></slot>
      </span>
    );
  }
}
